<!--
 * @Author: Merlin
 * @Date: 2024-08-23 11:45:57
 * @LastEditors: Merlin
 * @LastEditTime: 2024-11-14 15:26:05
 * @Description: 
-->
<template>
    <component
        :is="isBeingDialog ? DialogModalTitle : 'section'" 
        :model-value="modelValue"
        @update:model-value="handleUpdateModelValue"
        container-class="!w-[360px] max-w-[96vw]"
    >
        <template #header
            v-if="isBeingDialog"
        >
            <span class="text-[20px] leading-10">
                {{ $t('waves.settingDialogTitle') }} 
            </span>
        </template>
        <!-- 
            ↑ 顶部标题栏
            ↓ 内容区
        -->
        <div class="take-stop-info">
            <div class="take-stop-info__part">
                <div class="take-stop-info__title">
                    {{ $t('waves.TakeProfitAt') }}
                    &nbsp;
                <ClientOnly>
                    <Popper
                        v-if="isBeingDialog"
                        :style="oPopperStyle"
                        :hover="true"
                        :arrow="true"
                        placement="top"
                    >
                        <IconSvgUse
                            use="icon-question"
                            class="w-[16px] h-[16px] opacity-50"
                        />
                        <template #content>
                            <div class="popper-content">
                                {{ $t('waves.settingDialogTip4Profit') }} 
                            </div>
                        </template>
                    </Popper>
                </ClientOnly>
                </div>
                <div class="take-stop-info__inputs">
                    <div class="input input_md" style="--1e92a94a: 22px;">
                        <InputBoxAmount
                            prefix=""
                            class="input-price"
                            v-model="oForm_Ce.takeProfit" 
                            :placeholder="$t('waves.Price')" 
                            :tailLong="[2, iPrecision]"
                            @hasFocused="toFocus(1)"
                        />
                    </div>
                    <div class="input input_md" style="--1e92a94a: 22px;">
                        <InputBoxAmount
                            class="input-profit"
                            v-model="oForm_Ce.profit" 
                            :placeholder="'+ ' + $t('waves.Profit')" 
                            :prefix="String(oForm_Ce.profit ?? '') ? '+' : ''"
                            :tailLong="[2, iDecimalPlacesBy2 + 1]"
                            @hasFocused="toFocus(2)"
                        />
                    </div>
                </div>
                <!-- ↓ 输入错误时显示提示 -->
                <p class="take-stop-info__error"
                    v-if="oForm_Ce.takeProfitError || oForm_Ce.profitError"
                >
                    {{ 
                        $t(`waves.${oForm_Ce.takeProfitError || oForm_Ce.profitError}`)
                    }}
                </p>
            </div>
            <!------------------------------------->
            <!---------- ↑ 盈亏分界 ↓ ------------------>
            <!------------------------------------->
            <div class="take-stop-info__part">
                <div class="take-stop-info__title">
                    {{ $t('waves.CloseBetAt') }}
                    &nbsp;
                <ClientOnly>
                    <Popper
                        v-if="isBeingDialog"
                        :style="oPopperStyle"
                        :hover="true"
                        :arrow="true"
                        placement="top"
                    >
                        <IconSvgUse
                            use="icon-question"
                            class="w-[16px] h-[16px] opacity-50"
                        />
                        <template #content>
                            <div class="popper-content">
                                {{ $t('waves.settingDialogTip4Loss') }} 
                            </div>
                        </template>
                    </Popper>
                </ClientOnly>
                </div>
                <div class="take-stop-info__inputs">
                    <div class="input input_md" style="--1e92a94a: 22px;">
                        <InputBoxAmount
                            prefix=""
                            class="input-price"
                            v-model="oForm_Ce.closeBet" 
                            :placeholder="$t('waves.Price')" 
                            :tailLong="[2, iPrecision]"
                            @hasFocused="toFocus(3)"
                        />
                    </div>
                    <div class="input input_md" style="--1e92a94a: 22px;">
                        <InputBoxAmount
                            class="input-loss"
                            v-model="oForm_Ce.loss" 
                            :placeholder="'- ' + $t('waves.Loss')" 
                            :allowNegative="true" 
                            :prefix="String(oForm_Ce.loss ?? '') ? '-' : ''" 
                            :tailLong="[2, iDecimalPlacesBy2 + 1]"
                            @hasFocused="toFocus(4)"
                        />
                    </div>
                </div>
                <!-- ↓ 输入错误时显示提示 -->
                <p class="take-stop-info__error"
                    v-if="oForm_Ce.closeBetError || oForm_Ce.lossError"
                >
                    {{ 
                        $t(`waves.${oForm_Ce.closeBetError || oForm_Ce.lossError}`)
                    }}
                </p>
            </div>
            <p class="state-wrong take-stop-info__error" 
                v-if="oActiveContractInfo?.isClosed"
            >
                {{
                    $t(`waves.futures.errors.hasClosed`)
                    + $t(`waves.${oActiveContractInfo?.stateRealtime}`) 
                }}
            </p>
        </div>
        <!--
            ↑ 内容区
            ↓ 底部按钮区 
        -->
        <template v-slot:footer v-if="isBeingDialog">
            <ButtonThe3DButton
                color="red"
                class="w-full h-12"
                :disabled="!isUpdatable"
                @click="toUpdateOnePosition"
            >
                {{ $t('waves.settingDialogFooterButton') }} 
            </ButtonThe3DButton>
        </template>
    </component>
</template>

<script lang="ts" setup>
import type { WavesHistory, WavesHistoryPlus } from '@/socket/game/dto/waves.dto';
import TheModalTitle from '@/components/Modal/Title.vue'; 
import Popper from "vue3-popper"; 
import {
    fnGetChartStateToolbox_oi,
    oWavesBetForm_a, 
    fnGameForm_Ot,
    settingsState_f,
    fnSettingCenter_Ft, 
    oPositionCenter_yt,
    oPositionData_i,
} from '../ts/wavesControlCenter'; 
import {
    getOppositeValue, 
    getAfterPnlNetProfit, 
    fnGetLiquidationPrice_yO, 
    fnGetPriceMoveRate4Coeff, 
    fnGetCoeff, 
    getContolType, 
    getActiveContractInfo,
} from '../ts/wavesFn';  
import {
    oTickOptions,
    iDaySeconds,
    oTriggerType,
    oLongShort,
    oPnlFlat, 
} from '../ts/wavesData';  


const props = defineProps({
    type: {
        type: String as PropType<'dialog' | ''>,
        default: '',
    },
    modelValue: { // 窗口模式时控制自身可见性， 
        type: Boolean,
    },
    modalData: {
        // ↓ 下注后的 🔶 “生效的合约” 的专用数据，其它阶段不会收到参数
        // ↓ 游戏前此值为 undefined，游戏后在真实记录中此值是子键如下： 
        // ↓ 键名：wager, multi, positionAmount, type, feeType, pairCurrency, entryPrice  
        type: Object as PropType<WavesHistoryPlus>, 
    },
});

const oThePositionCenter = oPositionCenter_yt();
const oChartStateToolbox = fnGetChartStateToolbox_oi();
const oGameFormAndFn = fnGameForm_Ot();
const oSettingCenter = fnSettingCenter_Ft();
// --- ↑ xxCenter ---------------------------------------- 
const emits = defineEmits(['update:modelValue']); 
const DialogModalTitle = markRaw(TheModalTitle); 
const iFocusedA = ref(0); // 1-2
const iFocusedB = ref(0); // 3-4
const oErrors = ({
    // ↓ 报错信息：是 i18n 的键名  
    takeProfitError: '',
    closeBetError: '',
    profitError: '',
    lossError: '',
});
const oEmptyValue = ({
    // ↓ 输入框：1，2，3，4 
    // ↓ 止盈点数、止盈金额、止损点数、止损金额 
    takeProfit: '',
    profit: '',
    closeBet: '',
    loss: '',
});

// ↓ 关键表单 
const oForm_Ce = reactive({ 
    ...oEmptyValue, 
    ...oErrors,
});

// ============================================================================================ 
// =================== ↑ 声明区 ref, toRefs, reactive ==================== 
// =================== ↓ 计算区 computed() =============================== 
// ============================================================================================ 

const isBeingDialog = computed(()=> {
    return props.type === 'dialog';
}); 


// ↓ 表单输入有误
const isInputWrong = computed(() => {
    const arr = [
        oForm_Ce.takeProfitError,
        oForm_Ce.closeBetError,
        oForm_Ce.profitError,
        oForm_Ce.lossError,
        // ↓ 用于真实合约：合约止盈了或爆仓了等，总之就是关闭了
        (
            isBeingDialog.value
            && oActiveContractInfo.value?.isClosed
        ),
    ];
    const bResult = arr.some(Boolean);
    if (1 && import.meta.dev && bResult){
        console.log("isInputWrong 表单值有误不可建仓或修改 ↓\n", deepCopy(arr));
    }
    return bResult;
}); 

// ↓ 判断是否可修改了，仅用于窗口模式 
const isUpdatable = computed(()=>{
    const oModal = props.modalData; 
    if (!oModal) return; // 不是窗口模式
    if (isInputWrong.value){
        return false; // 输入值有误，禁止更新
    }
    const aCheckList = [
        (oForm_Ce.takeProfit != oModal.takeProfitPrice) && (oForm_Ce.takeProfit || oModal.takeProfitPrice),
        (oForm_Ce.profit != oModal.takeProfitAmount) && (oForm_Ce.profit || oModal.takeProfitAmount),
        (oForm_Ce.closeBet != oModal.stopLossPrice) && (oForm_Ce.closeBet || oModal.stopLossPrice),
        (oForm_Ce.loss != oModal.stopLossAmount) && (oForm_Ce.loss || oModal.stopLossAmount),
    ];
    // console.log("aCheckList ↓\n", deepCopy(aCheckList)); 
    const bResult = aCheckList.some(Boolean);
    return bResult;
});


// ----- ↓ 💘 入参提取与替补值专区 -----------------------
// ----- ↓ 💘 Also as：合约值提取与替补值专区 -----------------------

// ● wager
const wagerBy2_pe = computed(() => {
    const result = (
        props.modalData?.wager // 真实合约 
        || oWavesBetForm_a.wager // 游戏面板 
    );
    return Number(result);
});
// ● multi
const multiBy2_fe = computed(() => { 
    return Number(
        props.modalData?.multi
        || oWavesBetForm_a.multi
    );
});
// ● 杠杆后总仓值 
// ● positionAmount
const positionAmountBy2_ce = computed(() => {
    // 在对称赋值时，此值做为 position 值传入， 
    const aa = props.modalData?.positionAmount; 
    const bb = oGameFormAndFn.positionAmount.value; 
    return aa || bb; 
});
// ● type
const typeBy2_me = computed(() => {
    const aa = props.modalData?.type;
    const bb = oWavesBetForm_a.positionType;
    return aa || bb;
});

// ● feeType
const feeTypeBy2_ke = computed(() => {
    const aa = props.modalData?.feeType;
    const bb = oWavesBetForm_a.feeType;
    return aa || bb;
});

// ● pairCurrency
const pairCurrencyBy2_Pe = computed(() => {
    const aa = props.modalData?.pair; 
    // selectedPair 示例： FuryWaves_USDT 
    const bb = oChartStateToolbox.chartState.selectedPair;
    return aa || bb;
});

// ● entryPrice
// ● 入场价，或当前币最新点数值 
// 注意： be.value 和 props.modalData?.entryPrice 两项值雷同，有重叠部分  
const entryPriceBy2_be = computed(() => {
    // ↓ 真实合约入场价
    const aa = props.modalData?.entryPrice;
    // ↓ 当前币种最新价  
    const bb = oChartStateToolbox.currentPriceByPair.value || 0;
    // console.log("真实合约入场价：💥", aa); 
    return aa || bb;
}); 

// ---- ↑↑↑ 💘入参提取与替补值专区 ----------------------- 
// ---- ↓↓↓ 配置项提取专区  ----------------------- 

// 原版在顶部声明： currentPairSettings: ae, 
// ● bustBuffer 
const bustBuffer_Ee = computed(() => {
    // 做为 bustBuffer 传入到方法，用于计算对向值  
    const result01 = oSettingCenter.currentPairSettings.value?.bustBuffer; 
    const fBackup = 0.0003416634436;
    return result01 || fBackup;
});

// ● bufferMultiplier  
const bufferMultiplier_ge = computed(() => {
    // return ae.value?.bufferMultiplier || 1;
    const result01 = oSettingCenter.currentPairSettings.value?.bufferMultiplier; 
    return result01 || 1;
});

// ↓ 下注金额小数位数 
const iDecimalPlacesBy2 = computed(()=>{
    let iResult = 0;
    if (isBeingDialog.value){
        iResult = props.modalData?.iDecimalPlaces;
    }else{
        const oResult = oSettingCenter.curPairSettingsAndCurrency.value; 
        // console.log("当前下注币种💲配置对象↓", deepCopy(oResult));
        iResult = oResult?.iDecimalPlaces;
    }
    iResult ||= 2;
    return Math.max(2, iResult); 
});


// ===== ↓↓↓ 🩵🩵🩵 真实合约专区 📜 =========================--- 
// ===== ↓↓↓ 🩵🩵🩵 真实合约专区 📜 =========================--- 


const oActiveContractInfo = computed(()=>{
    if (isBeingDialog.value){
        return getActiveContractInfo(props.modalData); 
    }
});

// ↓ 这是 "真实合约" 的币种的当前最新价格 
// ↓ 四大监听有监听此值  
const fContractPairPrice = computed(()=>{
    // ↓ 取得一个对象，内含多个币种的最新价格信息 value, 和涨跌标记 status 
    // ↓ 例如： {ADA_USDT : {status: 0, value: 0.40087061}, ...} 
    const oCurrentPairsPrices = oChartStateToolbox.chartState.currentPairsPrices || {}; 
    // ↓ 取得“真实合约” 的币种名称 
    const sPairKey = props.modalData?.pair; 
    const oTargetPair = oCurrentPairsPrices[sPairKey]; 
    // console.log("合约币种信息：", deepCopy(oTargetPair)); 
    // ↓ 注意：☢️ 在下方要取 value 不要取 status 
    return oTargetPair?.value; 
});


// ↓ 当前合约的 after-pnl 利润值（赔钱时其值为负） 
// ↓ 在买涨买跌时，止盈金额 < 此值都会报错：盈利额不能小于合约 P&L 值  
// ↓ 也就是说，当前合约目前盈利 10 元，用户想设定在盈利 8 元时止盈，提示错误 
// ↓ 无合约时此值为一直为 0
const fAfterPnlNetProfit_$e = computed(() => {
    const oModal = props.modalData; 
    if (!oModal) {
        if (isBeingDialog.value){
            console.log("两值不一致：", isBeingDialog.value && oModal);
        }
        return 0;
    }
    const obj = {
        entryPrice: oModal.entryPrice, // 合约入场值 
        exitPrice: fContractPairPrice.value || 0, // 合约平仓值（持续跳动的当前的最新值）
        position: oModal.positionAmount,
        wager: oModal.wager,
        positionType: oModal.type, // 合约涨跌类型
        feeType: oModal.feeType,
        coeff: fCoeffOfContractPair_We.value,
    };
    const fResult = getAfterPnlNetProfit(obj); 
    return fResult; 
});

// ↓ 取得“当前合约” 的 coeff 值。它表示净利润分成比例，大约值：0.9xx 
// ↓ 做为 coeff 值，参数计算对向值  
const fCoeffOfContractPair_We = computed(() => { 
    const oModal = props.modalData; 
    if (!oModal) return 0; 
    const sFeeType = `${oModal.feeType}Fee`; 
    // ↓ 取得一个对象
    // ↓ 对象形态： { "1": { "0": 0.1, "1": 0.89, ...} }
    const oCoeffRates = oSettingCenter.currentPairSettings.value[sFeeType]; 
    // ↓ 取得退场点数，或利用止损金额反推此点数, 
    const exitPrice = (()=>{
        const takeProfit = Number(oForm_Ce.takeProfit);
        const profit = Number(oForm_Ce.profit);
        if (takeProfit > 0){
            return takeProfit;
        }else if (profit > 0){
            // 在此应对用户输入止损金额有误时（如输入值小于当前合约利润值）被消掉止损点数的情况 
            const fTakeProfit = fn2ndOppositeVal_Ye({
                iInputVal: profit,
                iInputIndex: 2,
                iVal2Compare: fContractPairPrice.value,
                getValOnly: true, 
            });
            return fTakeProfit;
        }
        return 0;
    })();
    const priceMove = fnGetPriceMoveRate4Coeff({
        entryPrice: oModal.entryPrice || 0,
        // ↓ 原版：退场值来自当前用户输入值，或来自止损金额的反推值（但后者经常因报错被消掉） 
        // exitPrice: Number(oForm_Ce.takeProfit) || 0, 
        exitPrice, 
    }); 
    // console.log("真实合约 oCoeffRates：", oCoeffRates); 
    const fCoeff = fnGetCoeff({ 
        amount: oModal.positionAmount || 0, 
        coeffRates: oCoeffRates,
        priceMove, 
        currency: oModal.currency,
    });
    return fCoeff;
});

// ===== ↑↑↑↑↑↑ 🩵🩵🩵 真实合约专区 =========================--- 
// ===== ↑↑↑↑↑↑ 🩵🩵🩵 真实合约专区 =========================--- 

// ↓ 小数长度（用于显示股价）
const iPrecision = computed(()=>{
    const pairsSettings = settingsState_f.pairsSettings;
    const oSetting = pairsSettings?.[pairCurrencyBy2_Pe.value];
    if (oSetting){
        return oSetting.precision
    }
    return 2; 
});

// ↓ 爆仓点位值，
// ↓ 用户输入的“止损点数” 的比较值 
// ↓ 买涨时：输入点数 < 此值，报错：futures.errors.lossPriceBust = 止损价格不能超过爆仓价格 
// ↓ 买跌时：输入点数 > 此值，报错： 同上 
// 例如：点数跌到 80元时就爆仓了，此时设定跌至 70元时止损平仓，不合理 
const fLiquidationnPrice = computed(() => { 
    const obj01 = { 
        entryPrice: entryPriceBy2_be.value,
        positionType: typeBy2_me.value,
        multi: multiBy2_fe.value,
        bustBuffer: bustBuffer_Ee.value,
        bufferMultiplier: bufferMultiplier_ge.value,
    };
    const fResult = fnGetLiquidationPrice_yO(obj01);
    return fResult;
});

// ↓ 杠杆后的总仓值 x 万分之5 
// ↓ 买涨买跌时共有2次调用，当：止损金额 < 此值，将错如下： 
// ↓ futures.errors.lossHigherBet ● 止损价格太过接近当前价格 
const positionShrinked_ye = computed(() => { 
    const n01 = 5 / 10_000; // ← 万分之5
    const fResult = n01 * positionAmountBy2_ce.value; 
    return fResult;
});

// He.value 
// ↓ 返回数值，做为 coeff 值候选项之一 
// ↓ 止盈金额输入后调用此值参与校验 
const fCoeffOfCurrentPair_He = computed(() => {
    // ↓ 入场点数值（首选真实合约入场值 || 当前币种最股价） 
    const entryPrice = entryPriceBy2_be.value; 
    // ↓ 取得退场点数，或利用止损金额反推此点数, 
    const exitPrice = (()=>{
        const takeProfit = Number(oForm_Ce.takeProfit);
        const profit = Number(oForm_Ce.profit);
        if (takeProfit > 0){
            return takeProfit;
        }else if (profit > 0){
            // 在此应对用户输入止盈金额有误时（如输入值小于当前合约利润值）被消掉止损点数的情况 
            const fTakeProfit = fn2ndOppositeVal_Ye({ 
                iInputVal: profit,
                iInputIndex: 2,
                iVal2Compare: entryPrice, 
                getValOnly: true, 
            });
            return fTakeProfit;
        }
        return 0;
    })();
    const priceMove = fnGetPriceMoveRate4Coeff({
        entryPrice,
        exitPrice, // exitPrice: Number(oForm_Ce.takeProfit),
    });
    const oParams = { 
        coeffRates: oSettingCenter.currentPairCoeffValues.value, // ue.value, 
        amount: positionAmountBy2_ce.value,
        priceMove,
        // currency: '', // 不需要传递 
    };
    const fResult = fnGetCoeff(oParams);
    return fResult;
});

// ↓ 最终的 coeff 值， 
const fCoeffBy2 = computed(()=>{
    const fResult = (
        props.modalData // 窗口模式 
        ? fCoeffOfContractPair_We.value // 用于真实合约
        : fCoeffOfCurrentPair_He.value // 其它 
    );
    return fResult;
});

// ============================================================================================
// =================== ↑ 计算区 computed() ===============================
// =================== ↓ 方法 ============================================ 
// ============================================================================================

const handleUpdateModelValue = (bNewValue) => { 
    emits('update:modelValue', !!bNewValue); 
}

// ↓ 建仓后修改 🔶 
const toUpdateOnePosition = async () => {
    console.log("表单输入正确性：", !isInputWrong.value); 
    // ↓ 调试阶段放弃 return 
    if (isInputWrong.value && !import.meta.dev) 
        return;
    const res = await oThePositionCenter.updatePosition({
        positionId: props.modalData._id,
    });
    if (!res || res['error']){ 
        return; 
    }
    console.log("建仓后修改：", deepCopy(res)); 
    handleUpdateModelValue(false); // 关闭窗口 
}

// ↓ 记录输入焦点 
const toFocus = (indexOfInput) => {
    // console.log("聚焦输入框：", indexOfInput); 
    if ([1, 2].includes(indexOfInput)){
        iFocusedA.value = indexOfInput;
    }else if ([3, 4].includes(indexOfInput)){
        iFocusedB.value = indexOfInput;
    }
};


// ↓ 检查输入值正确性，并显示提示内容 
const fn1stCheckInputVal_qe = (oNew)=>{ 
    let {
        iInputVal, // 输入框的数值
        iInputIndex, // 输入框编号 1-4
        // -- ↓ 以下2项是最新点数值，当前币与合约币 ---------------------------- 
        iVal2Compare, // 当前币最新点数 
        // ↓ 仅在真实合约中可收到此值，否则值为 NaN 
        // ↓ 也是最新点数值，但它是真实合约币种的最新点数值， 
        iModalCurrent = NaN,
    } = oNew;
    if (!iInputIndex) return; 
    const wager = wagerBy2_pe.value; // 本金 
    const contractType = typeBy2_me.value; // Long or Short 
    // ------------------------------------------ 
    // ↓ 用于判定是否输入错误 
    // ↓ 说明：至少要输入了某值，才开始怀疑此值或为错值  
    let isValueWrong = !!iInputVal;
    // --- ↓ 针对止盈点数 --------- 
    if (iInputIndex === 1) {
        oForm_Ce.takeProfitError = wager ? '' : 'futures.errors.emptyWager';  // 原版中文：押注数量为空白 
        if (!wager) return; 
        if (contractType === oLongShort.long) {
            isValueWrong &&= (
                // ↓ 输入有误：买涨模式止盈点数，不应 < 当前最新点数（最后改为 <= ☢️ 如同真实合约中的运算符 <=)
                iInputVal <= iVal2Compare
                || (iModalCurrent && iInputVal <= iModalCurrent) // 真实合约时：也不应 < 当前最新点数 
            );
            oForm_Ce.takeProfitError = (
                isValueWrong
                ? 'futures.errors.profitPriceLow'
                : ''
            );
        }
        if (contractType === oLongShort.short) {
            isValueWrong &&= (
                // ↓ 输入有误：买跌模式止盈点数 > 当前最新点数（最后改为 >= ☢️ 如同真实合约中的运算符 >= ) 
                iInputVal >= iVal2Compare
                || (iModalCurrent && iInputVal >= iModalCurrent) // 真实合约时：也不应 > 当前最新点数 
            );
            oForm_Ce.takeProfitError = (
                isValueWrong
                ? 'futures.errors.profitPriceHigh'
                : ''
            );
        }
    } 
    // --- ↓ 针对止盈金额 --------- 
    else if (iInputIndex === 2){
        oForm_Ce.profitError = wager ? '' : 'futures.errors.emptyWager'; 
        if (!wager) return; 
        // ↓ 目前发现仅在生成的合约中取到值 
        // console.log("预估利润值：", fAfterPnlNetProfit_$e.value);
        const sError02 = (
            (
                iInputVal
                && fAfterPnlNetProfit_$e.value // ← 仅在真实合约中才有正式值，否则为 0
                // ↓ 输入有误：某合约当前盈利是 10 元，玩家设定盈利8元时止盈平仓，不合理 
                && (iInputVal < fAfterPnlNetProfit_$e.value)
            )
            ? 'futures.errors.profitLow' // == 盈利额不能小于 P&L 
            : ''
        );
        oForm_Ce.profitError = (
            iInputVal && iInputVal < 0
            ? 'futures.errors.profitNegative' 
            : sError02
        );
        // --- ↓ 买跌止盈金额监测区 ---------------------------- 
        if (contractType === oLongShort.short) { 
            const fCoeffVal = fCoeffBy2.value;
            // ↓ 这是当前止盈目标对应的“理论下注金额” 💥 需要此值 < 杠杆后总仓值才属于合理范围，并据此认定用户输入无误 
            // ↓ 反之：杠杆后总仓值 <= 此值，属于异常范围，将报错 （如原版公式，如下） 
            // ↓ 总结：此值是要实现当前盈利目标值的一个下注总仓值，例如要止盈 45，得有 50 元的总仓才能达成目标 
            // ↓ 换言之： 此值 >= 杠杆后总仓值，将报错（等效公式）  
            const fPositionValueInTheory = ( 
                feeTypeBy2_ke.value === oPnlFlat.pnl 
                // ↓ 仅在 pnl 计费模式应用系数（coeff) 
                // 1️⃣ 示例：输入值47.5 / 系数值0.95 = 放大值50 
                // 上方示例假设某个买跌合约杠杠后金额是 49.9元。在输入止盈金额为 47.5 元时据此推算需要总盈利 50元才能达成该止盈目标 
                // 那么这个 50 已经 >= 杠杆后的总仓值(盈利的极限) 超出合理范围，会报错 
                // 用户需要下调止盈金额为 47.4 / 0.95 = 49.8 此为合理的输入。因为放大后的止盈值 < 杠杆后的总仓值才属于合理范围 
                // 2️⃣ 示例：102 / 1.02 = 100 ◆ 替补值 1.02 将“缩小” 输入值 102 缩到 100；意图不明，有点不解 
                // ↓ 下方的替补值从原版的 1.02 改为了 0.9 那么 102 / 0.9 = 113.33 表示下注 113.33 才有可能实现当前止盈目标 
                ? iInputVal / (fCoeffVal || 0.9) 
                // ↓ 仅在 flat 计费模式使用用户输入的原始值  
                : iInputVal
            );
            if (0 && iInputVal && import.meta.dev){
                console.log("实现当前止盈目标所需下注金额：", iInputVal / fCoeffVal, `(${iInputVal}/${fCoeffVal})`); 
            }
            oForm_Ce.profitError = (
                // ↓ 输入有误：杠杆后的总仓值如果 <= 当前输入值，则报错 
                (
                    positionAmountBy2_ce.value <= fPositionValueInTheory 
                    // ↓ 后补：止盈金额不能 >= 总仓值（买跌模式下，总仓值即为盈利上限(且需要点位跌到0)，故而不能将止盈金额设定为盈利上限值  
                    || positionAmountBy2_ce.value <= iInputVal 
                ) 
                // ↓ 点数跌到0你也赚不到这个数，输入的太大了！！！ 
                // ↓ 例如杠杆后的总仓值 100元，盈利金额不可能超过此值   
                ? 'futures.errors.takeProfitNegative' // == 止盈价格不能小于零（疑似原文这里说的是止盈点数） 
                : ''
            );
        }
    }
    // --- ↓ 针对止损点数 ---------
    else if (iInputIndex === 3){ 
        oForm_Ce.closeBetError = wager ? '' : 'futures.errors.emptyWager'; 
        if (!wager) return; 
        if (contractType === oLongShort.long) {
            const v02 = (
                iInputVal
                // ↓ 输入有误：止损点 < 小于爆仓点数不合理
                // ↓ 如果跌到 70 已经爆仓，此时设定跌至 80 点时再止损，不合理，不是止损，是必损，
                && iInputVal < fLiquidationnPrice.value
                ? 'futures.errors.lossPriceBust'
                : ''
            );
            oForm_Ce.closeBetError = (
                iInputVal
                // ↓ 输入有误：点数 > 当前点数; 大于将盈，没有止损效果 
                && iInputVal > iVal2Compare
                ? 'futures.errors.lossPriceHigh'
                : v02
            );
        }
        if (contractType === oLongShort.short) {
            const v03 = (
                iInputVal
                // ↓ 输入有误：止损点数 > 大于爆仓点数；
                // ↓ 涨到 80 点已经爆仓，此时设定涨至 90 点时再止损，不合理，不是止损，是必损 
                && iInputVal > fLiquidationnPrice.value
                ? 'futures.errors.lossPriceBust'
                : ''
            );
            oForm_Ce.closeBetError = (
                iInputVal
                // ↓ 输入有误：点数 < 当前点数则赚，不是止损，
                && iInputVal < iVal2Compare
                ? 'futures.errors.lossPriceLow' // === 原文"止损价格不能低于当前价格" 
                : v03
            );
        }
    }
    // --- ↓ 针对止损金额 --------- 
    else if (iInputIndex === 4){
        oForm_Ce.lossError = wager ? '' : 'futures.errors.emptyWager'; 
        if (!wager) return; 
        // ↓ 注意这里取 3️⃣ 号输入框的值 
        // ↓ 原版从本地记录取值，导致了交互行为错误，在此改为通过方法计算得出对向值（计算出3️⃣号输入框的值） 
        // ↓ 这是一个推算值，推算得出对向输入框的新值， 
        const fCloseBet = fn2ndOppositeVal_Ye({
            iInputVal,
            iInputIndex,
            iVal2Compare,
            getValOnly: true, 
        });
        const hasBothVal = iInputVal && fCloseBet;
        // ↓ 输入有误： 设定的止损金额 >= 保证金，不合理  
        // ↓ 例如一共下注 10 元，并设定在损失 11 元时平仓，不合理 
        const loose2Much = iInputVal >= wager;
        // ↓ 输入有误：止损金额不能设得太小，例如押注一万，并设定止损金额仅为1元 
        // ↓ 将提示：止损价格太过接近当前价格 
        const isAmountToMall = (
            iInputVal
            && iInputVal <= positionShrinked_ye.value
        ); 
        if (contractType === oLongShort.long) {
            // 这里大可用输入值比较杠杆后总仓值，输入值必需小于总仓值方可，
            const v02 = (
                loose2Much || 
                // ↓ 输入有误：止损点数 < 爆仓点数则报错：
                // ↓ 跌至 80 点就已爆仓，此时设定跌至 70 点时再止损不合理
                (
                    hasBothVal
                    && fCloseBet < fLiquidationnPrice.value 
                )
                ? 'futures.errors.lossPriceBust'
                : ''
            ); 
            oForm_Ce.lossError = (
                isAmountToMall
                ? 'futures.errors.lossHigherBet'
                : v02
            );
        }
        if (contractType === oLongShort.short) {
            const v03 = (
                loose2Much ||
                // ↓ 输入有误：止损点数 > 爆仓点数则报错：
                // ↓ 涨至 80 点就已爆仓，此时设定涨至 90 点时再止损不合理 
                (
                    hasBothVal
                    && fCloseBet > fLiquidationnPrice.value 
                ) 
                ? 'futures.errors.lossPriceBust'
                : ''
            );
            oForm_Ce.lossError = (
                isAmountToMall
                ? 'futures.errors.lossHigherBet'
                : v03
            );
        }
    }
    // ↓ 游戏面板专用赋值 
    if (!isBeingDialog.value){
        oGameFormAndFn.setIsTakeStopError(isInputWrong.value);
    }
};

// ↓ 计算对向值（也会消除对向值） 
const fn2ndOppositeVal_Ye = (oNew)=>{ 
    if (!wagerBy2_pe.value || !multiBy2_fe.value) {
        return;
    }
    let {
        iInputVal,
        iInputIndex,
        iVal2Compare,
        getValOnly=false,
    } = oNew;
    // // ↓ 注意，此处声明可能得到一个假性值， 或负值 
    const fOppositeVal = getOppositeValue({ 
        index: iInputIndex, // 输入框编号 
        triggerPrice: Number(iInputVal), // 输入框数值 
        // ----------------------- 
        positionType: typeBy2_me.value, // long, short 
        feeType: feeTypeBy2_ke.value, // pnl, flat 
        // ----------------------- 
        position: positionAmountBy2_ce.value, 
        coeff: fCoeffBy2.value || void 0, // void 0 触发函数的默认值 
        currentPrice: iVal2Compare, 
        bustBuffer: bustBuffer_Ee.value,
    });
    // console.log("正在聚焦x号输入框，与对向值：", iInputIndex, '=>', fOppositeVal);
    if (getValOnly === true) {
        return fOppositeVal;
    }
    if (1 === iInputIndex) {
        if (oForm_Ce.takeProfitError){
            oForm_Ce.profit = '';
        } else if (fOppositeVal){
            oForm_Ce.profit = String(fOppositeVal);
        }else{
            oForm_Ce.profit &&= '';
        }
        oForm_Ce.profitError = '';
    }
    if (2 === iInputIndex) {
        if (oForm_Ce.profitError){
            oForm_Ce.takeProfit = '';
        } else if (fOppositeVal > 0){
            oForm_Ce.takeProfit = String(fOppositeVal);
        }else{
            oForm_Ce.takeProfit &&= '';
        }
        oForm_Ce.takeProfitError = '';
    }
    // 1️⃣ takeProfit, 2️⃣ profit, 3️⃣ closeBet, 4️⃣loss 
    if (3 === iInputIndex) { 
        if (oForm_Ce.closeBetError){
            oForm_Ce.loss = '';
        } else if (fOppositeVal){
            oForm_Ce.loss = String(Math.abs(fOppositeVal)); 
        }else{
            oForm_Ce.loss &&= '';
        }
        oForm_Ce.lossError = '';
    }
    if (4 === iInputIndex) {
        if (oForm_Ce.lossError){
            oForm_Ce.closeBet = '';
        }else if (fOppositeVal > 0){
            oForm_Ce.closeBet = String(fOppositeVal);
        }else{
            oForm_Ce.closeBet &&= '';
        }
        oForm_Ce.closeBetError = ''; 
    }
};

// ↓ 四大监听被触发后的调用方法 
const fnWatcherWorker = (aNew)=>{
    let [
        iInputVal, // 输入值
        iVal2Compare, // entryPrice 
        iInputIndex, // 输入框编号 1-4 
        iModalCurrent = NaN, // 
    ] = aNew;
    // console.log("当前最新点位：", iModalCurrent);
    // ---- ↓ 容错值，补充目标输入框的值 --------------------- 
    const backupValue = [ 
        '',
        oForm_Ce.takeProfit,
        oForm_Ce.profit,
        oForm_Ce.closeBet,
        oForm_Ce.loss,
    ][iInputIndex];
    iInputVal ||= backupValue;
    // console.log(`输入框：${iInputIndex}=${iInputVal}`);  
    const oNew = {
        iInputVal,
        iVal2Compare,
        iInputIndex,
        iModalCurrent,
    };
    // ↓ 校验，并显示提示内容 
    fn1stCheckInputVal_qe(oNew);
    // ↓ 设定对向值 
    fn2ndOppositeVal_Ye(oNew); 
};

// ↓ 充值表单数据，仅在用于在窗口模式执行表单初始化 
const fnInitFormData = (oModal) => {
    Object.assign(oForm_Ce, {
        ...oErrors, // 把报错复位 
        takeProfit: oModal.takeProfitPrice || "", 
        profit: oModal.takeProfitAmount || "", 
        closeBet: oModal.stopLossPrice || "", 
        loss: oModal.stopLossAmount || "", 
    });
    const {
        selectedTriggerProfit: sTriggerProfit='',
        selectedTriggerLoss: sTriggerLoss='',
    } = oModal;
    Object.assign(oWavesBetForm_a, {
        selectedTriggerProfitUpdate: sTriggerProfit,
        selectedTriggerLossUpdate: sTriggerLoss,
    });

    if (sTriggerProfit == oTriggerType.price){
        iFocusedA.value = 1; // 如果不赋值，会被判定为没有设定相关触发 
    }else if (sTriggerProfit == oTriggerType.value){
        iFocusedA.value = 2;
    }
    // ----------------------
    if (sTriggerLoss == oTriggerType.price){
        iFocusedB.value = 3;
    }else if (sTriggerLoss == oTriggerType.value){
        iFocusedB.value = 4;
    }
}


// 清空表单数据，一但取消涨跌设定，表单收缩这里执行清空 
const clearFormData = () => {
    Object.assign(oForm_Ce, {
        ...oErrors, // 把报错复位 
        ...oEmptyValue,
    });
    Object.assign(oWavesBetForm_a, {
        selectedTriggerLoss: oTriggerType.none,
        selectedTriggerProfit: oTriggerType.none,
    });
    // ↓ 赋值，用于表示止盈止损值无误（输入框都清空了，肯定的无误的） 
    oGameFormAndFn.setIsTakeStopError(false);
}

defineExpose({
    fnInitFormData,
    clearFormData, 
});

// ================= ↓ 输入4大验项 =============================  
// takeProfit, profit, closeBet, loss,  
watch(
    ()=>{
        const aResult = [
            oForm_Ce.takeProfit, // 1️⃣ 号值 
            entryPriceBy2_be.value, // ⚓ 真实合约入场点数，或当前币最新点数 
            iFocusedA.value, // ✒️ 输入焦点 
            fContractPairPrice.value, // 📊 最新点位（合约币种）
        ];
        return aResult;
    },
    (aNew)=>{
        if (aNew[2] != 1) return;
        fnWatcherWorker(aNew);
    },
);
watch(
    ()=>{
        const aResult = [
            oForm_Ce.profit, // 2️⃣号值
            entryPriceBy2_be.value,
            iFocusedA.value,
            fContractPairPrice.value,
        ];
        return aResult;
    },
    (aNew)=>{
        if (aNew[2] != 2) return;
        fnWatcherWorker(aNew);
    },
);
watch(
    ()=>{
        const aResult = [
            oForm_Ce.closeBet, // 3️⃣号值 
            entryPriceBy2_be.value,
            iFocusedB.value,
            fContractPairPrice.value,
        ];
        return aResult;
    },
    (aNew)=>{
        if (aNew[2] != 3) return;
        fnWatcherWorker(aNew);
    },
);
watch(
    ()=>{
        const aResult = [
            oForm_Ce.loss, // 4️⃣号值 
            entryPriceBy2_be.value,
            iFocusedB.value,
            fContractPairPrice.value,
        ];
        return aResult;
    },
    (aNew)=>{
        if (aNew[2] != 4) return;
        fnWatcherWorker(aNew);
    },
);
// ================= ↑ 输入4大验项 =============================  

watch(
    ()=>{
        // ↓ 监听输入框被修改 
        return [ 
            oForm_Ce.takeProfit,
            oForm_Ce.profit,
            oForm_Ce.closeBet,
            oForm_Ce.loss,
            iFocusedA.value, 
            iFocusedB.value, 
        ];
    },
    (aNew) => {
        const sType12 = (
            (aNew[0] || aNew[1])
            ? getContolType(iFocusedA.value)
            : ""
        );
        const sType34 = (
            (aNew[2] || aNew[3])
            ? getContolType(iFocusedB.value)
            : ""
        );
        // ↓ 表示在修改仓，不是建仓 
        const isUpdating = isBeingDialog.value;
        oGameFormAndFn.setTakeProfit(aNew[0], sType12, isUpdating);
        oGameFormAndFn.setTakeProfitAmount(aNew[1], sType12, isUpdating); 
        oGameFormAndFn.setStopLoss(aNew[2], sType34, isUpdating);
        oGameFormAndFn.setStopLossAmount(aNew[3], sType34, isUpdating); 
        // console.log('触发标记↓', deepCopy(oWavesBetForm_a));
    },
);

onMounted(()=>{ 
    if (!isBeingDialog.value) return;
    // ↓ 专门处理窗口模式 
    // ↓ 窗口打开时需要初始化表单数据 
    fnInitFormData(props.modalData);
    if (1 && import.meta.dev) {
        console.group('窗口已经打开');
        console.log("窗口收到数据：", deepCopy(props.modalData));
        console.log(
            "窗口表单赋值结果：",
            {
                ...oForm_Ce,
                selectedTriggerLossUpdate: oWavesBetForm_a.selectedTriggerLossUpdate,
                selectedTriggerProfitUpdate: oWavesBetForm_a.selectedTriggerProfitUpdate,
            },
        );
        console.groupEnd();
    }
});

/*  --- ↓ 12种数值错误提示速览，速查表 ---------- 
    --- ↓ profit ---- 
    futures.errors.profitLow ● 盈利额不能小于 P&L
    futures.errors.profitNegative ● 盈利额不能小于零
    futures.errors.profitPriceHigh ● 止盈价格不能高于当前价格
    futures.errors.profitPriceLow ● 止盈价格不能低于当前价格
    --- ↓ loss ---- 
    futures.errors.lossHigherBet ● 止损价格太过接近当前价格
    futures.errors.lossPriceBust ● 止损价格不能超过爆仓价格
    futures.errors.lossPriceHigh ● 止损价格不能高于当前价格
    futures.errors.lossPriceLow ● 止损价格不能低于当前价格
    --- ↓ others ---- 
    futures.errors.emptyWager ● 押注数量为空白
    futures.errors.insufficientBalance ● 所选货币余额不足
    futures.errors.outOfPositionLimit ● 合計持仓量已达上限
    futures.errors.takeProfitNegative ● 止盈价格不能小于零
*/

</script>


<style scoped lang="scss">

.popper-content{
    width: 220px;
    max-width: 90vw;
}

.take-stop-info__part{
    margin-top: 14px; 
}

.take-stop-info__title{
    color: var(--color_grey-20);
    display: flex;
    font-size: 12px;
    font-weight: 600;
    justify-items: center;
    letter-spacing: -.02em;
    line-height: 16px;
    margin-bottom: 4px;
    margin-top: 0; 
}

.take-stop-info__inputs{
    display: grid;
    grid-template-columns: repeat(2,1fr);
    grid-column-gap: 4px
}

.take-stop-info__error{
    color: var(--color_red);
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    margin-bottom: 0;
    margin-top: 14px
}

.input-price :deep(){
    input {
        color: white;
    }
}

.take-stop-info__inputs :deep(){
    .input-profit {
        color: var(--color_green);
    }
    .input-loss {
        color: var(--color_red);
    }
}

// ↓ 后补的合约状态出错提示
.state-wrong{
    margin-top: 5px;
    padding: 5px 0 5px;
}

</style>


